import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 모달 상태
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  
  // 폼 상태
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // 관리자 권한 확인
  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // 사용자 목록 조회
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/users', {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('사용자 목록을 가져오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('사용자 목록 조회 오류:', error);
      setError('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // 새 사용자 생성
  const handleCreateUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      setError('사용자명과 비밀번호를 모두 입력해주세요.');
      return;
    }

    if (newUser.username.trim().length < 3) {
      setError('사용자명은 3자 이상이어야 합니다.');
      return;
    }

    if (newUser.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          username: newUser.username.trim(),
          password: newUser.password
        })
      });

      if (response.ok) {
        setSuccess('새 관리자 계정이 생성되었습니다.');
        setCreateModalOpen(false);
        setNewUser({ username: '', password: '' });
        setError('');
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '계정 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('계정 생성 오류:', error);
      setError('서버 연결에 실패했습니다.');
    }
  };

  // 사용자 삭제
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.username}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSuccess('관리자 계정이 삭제되었습니다.');
        setDeleteModalOpen(false);
        setSelectedUser(null);
        setError('');
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '계정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('계정 삭제 오류:', error);
      setError('서버 연결에 실패했습니다.');
    }
  };

  // 비밀번호 변경
  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword.trim()) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }

    if (newPassword.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      const response = await fetch('/api/admin/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          username: selectedUser.username,
          new_password: newPassword
        })
      });

      if (response.ok) {
        setSuccess('비밀번호가 성공적으로 변경되었습니다.');
        setPasswordModalOpen(false);
        setSelectedUser(null);
        setNewPassword('');
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      setError('서버 연결에 실패했습니다.');
    }
  };

  // 모달 핸들러
  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
    setNewUser({ username: '', password: '' });
    setError('');
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedUser(null);
    setError('');
  };

  const handlePasswordModalClose = () => {
    setPasswordModalOpen(false);
    setSelectedUser(null);
    setNewPassword('');
    setError('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon sx={{ fontSize: 35 }} />
          관리자 계정 관리
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          sx={{ fontWeight: 600 }}
        >
          새 관리자 추가
        </Button>
      </Box>

      {/* 알림 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    전체 관리자
                  </Typography>
                  <Typography variant="h4">
                    {users.length}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    활성 계정
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(user => user.is_active).length}
                  </Typography>
                </Box>
                <SecurityIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 사용자 목록 테이블 */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>사용자명</TableCell>
                <TableCell>생성일</TableCell>
                <TableCell>최근 로그인</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    로딩 중...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    등록된 관리자가 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: 'text.secondary' }} />
                        {user.username}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {user.last_login ? new Date(user.last_login).toLocaleString() : '로그인 기록 없음'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? '활성' : '비활성'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => {
                          setSelectedUser(user);
                          setPasswordModalOpen(true);
                        }}
                        color="primary"
                        size="small"
                        title="비밀번호 변경"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteModalOpen(true);
                        }}
                        color="error"
                        size="small"
                        title="삭제"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 새 사용자 생성 모달 */}
      <Dialog open={createModalOpen} onClose={handleCreateModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>새 관리자 계정 생성</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="사용자명"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            margin="normal"
            helperText="3자 이상 입력해주세요"
          />
          <TextField
            fullWidth
            label="비밀번호"
            type={showPassword ? 'text' : 'password'}
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            margin="normal"
            helperText="6자 이상 입력해주세요"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateModalClose}>취소</Button>
          <Button onClick={handleCreateUser} variant="contained">생성</Button>
        </DialogActions>
      </Dialog>

      {/* 사용자 삭제 확인 모달 */}
      <Dialog open={deleteModalOpen} onClose={handleDeleteModalClose}>
        <DialogTitle>관리자 계정 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            정말로 '<strong>{selectedUser?.username}</strong>' 계정을 삭제하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteModalClose}>취소</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">삭제</Button>
        </DialogActions>
      </Dialog>

      {/* 비밀번호 변경 모달 */}
      <Dialog open={passwordModalOpen} onClose={handlePasswordModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>비밀번호 변경</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            '<strong>{selectedUser?.username}</strong>' 계정의 비밀번호를 변경합니다.
          </Typography>
          <TextField
            fullWidth
            label="새 비밀번호"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            helperText="6자 이상 입력해주세요"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordModalClose}>취소</Button>
          <Button onClick={handleChangePassword} variant="contained">변경</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPage;
